import { createStore } from "zustand/vanilla";

import type { SyncedTableName } from "@/lib/contract";

import { rebase } from "./local-mutations";
import {
  emptyTables,
  type ConnectionStatus,
  type PendingMutation,
  type Row,
  type SyncSession,
  type Tables,
} from "./types";

/**
 * The replica, in memory.
 *
 *   base     what the server has confirmed: rows from deltas, nothing else.
 *   pending  what the user has done that the server has not yet folded in.
 *   tables   base with pending replayed on top. The only thing the UI reads.
 *
 * Keeping the two layers apart is what makes optimistic updates safe. When the
 * server's version of a change arrives it goes into `base`, the pending
 * mutation is dropped, and `tables` is recomputed. A rejected mutation is
 * simply dropped, and its effect vanishes, with no hand-written undo for any
 * change.
 */
export interface SyncState {
  status: ConnectionStatus;
  session: SyncSession | null;
  base: Tables;
  pending: PendingMutation[];
  tables: Tables;
  lastVersion: number;
  /** True once the replica holds a complete picture, from storage or the server. */
  ready: boolean;
}

export const syncStore = createStore<SyncState>(() => ({
  status: "loading",
  session: null,
  base: emptyTables(),
  pending: [],
  tables: emptyTables(),
  lastVersion: 0,
  ready: false,
}));

function derive(state: Pick<SyncState, "base" | "pending" | "session">): Tables {
  return rebase(state.base, state.pending, state.session);
}

export function resetStore(session: SyncSession | null): void {
  syncStore.setState({
    status: "loading",
    session,
    base: emptyTables(),
    pending: [],
    tables: emptyTables(),
    lastVersion: 0,
    ready: false,
  });
}

export function hydrate(base: Tables, pending: PendingMutation[], lastVersion: number): void {
  const { session } = syncStore.getState();
  syncStore.setState({
    base,
    pending,
    lastVersion,
    tables: derive({ base, pending, session }),
    // A replica that has synced before is complete as of its version: show it
    // straight away, before the network has said a word. This is what makes
    // the app open instantly, and work offline.
    ready: lastVersion > 0,
  });
}

export function setStatus(status: ConnectionStatus): void {
  syncStore.setState({ status });
}

export interface RowChange {
  table: SyncedTableName;
  upserts: Row[];
  deletes: string[];
}

/**
 * Fold a server delta into `base`, then drop every pending mutation the
 * replica has now caught up with. Each table is copied once per batch, not
 * once per row, so a bootstrap of thousands of rows stays cheap.
 */
export function applyServerChanges(changes: readonly RowChange[], upTo: number): void {
  const state = syncStore.getState();
  const base = { ...state.base } as Record<SyncedTableName, Record<string, Row>>;

  for (const change of changes) {
    let table: Record<string, Row>;
    if (change.deletes.length === 0) {
      table = { ...base[change.table] };
    } else {
      // Copy without the deleted rows rather than deleting keys afterwards.
      const gone = new Set(change.deletes);
      table = {};
      for (const [id, row] of Object.entries(base[change.table])) {
        if (!gone.has(id)) table[id] = row;
      }
    }
    for (const row of change.upserts) table[row.id] = row;
    base[change.table] = table;
  }

  const lastVersion = Math.max(state.lastVersion, upTo);
  const pending = state.pending.filter(
    (mutation) => mutation.ackedVersion === null || mutation.ackedVersion > lastVersion,
  );

  syncStore.setState({
    base: base as Tables,
    pending,
    lastVersion,
    tables: derive({ base: base as Tables, pending, session: state.session }),
  });
}

export function markReady(): void {
  syncStore.setState({ ready: true });
}

export function addPending(mutation: PendingMutation): void {
  const state = syncStore.getState();
  const pending = [...state.pending, mutation];
  syncStore.setState({ pending, tables: derive({ ...state, pending }) });
}

/**
 * The server confirmed a mutation at `version`. If the replica is already at
 * or past it, the change is in `base` and the mutation can go now; otherwise
 * it stays until the delta that carries it arrives.
 */
export function ackPending(id: string, version: number): PendingMutation | undefined {
  const state = syncStore.getState();
  const mutation = state.pending.find((m) => m.id === id);
  if (!mutation) return undefined;

  const acked = { ...mutation, ackedVersion: version };
  const pending =
    version <= state.lastVersion
      ? state.pending.filter((m) => m.id !== id)
      : state.pending.map((m) => (m.id === id ? acked : m));

  syncStore.setState({ pending, tables: derive({ ...state, pending }) });
  return acked;
}

/** The server refused a mutation for good. Its optimistic effect disappears. */
export function dropPending(id: string): PendingMutation | undefined {
  const state = syncStore.getState();
  const mutation = state.pending.find((m) => m.id === id);
  if (!mutation) return undefined;
  const pending = state.pending.filter((m) => m.id !== id);
  syncStore.setState({ pending, tables: derive({ ...state, pending }) });
  return mutation;
}
